<div class="tix-auth flex flex-col space-y-4 pb-8">
  <ng-container *ngIf="step === 1">
    <tix-card-header class="flex flex-col space-y-2 mb-3 bg-transparent">
      <tix-card-title class="text-lg text-black font-bold">{{
        !isEditMode ? 'Create an Account' : 'Update Account'
      }}</tix-card-title>
      <p class="text-[#72787E] w-full" *ngIf="!isEditMode">
        Please fill in the following information to create an account, or for
        returning users
        <a
          class="text-primary underline font-bold hover:text-matprimary-700"
          [routerLink]="LOGIN_ROUTE"
          queryParamsHandling="preserve"
          >login</a
        >.
      </p>
    </tix-card-header>
    <tix-card-content *ngIf="!isEditMode" class="tix-register-card-content">
      <!-- Registration Form (Non-Edit Mode) -->
      <form [formGroup]="registerForm" class="flex flex-col">
        <mat-form-field hideRequiredMarker="true">
          <mat-label>Email</mat-label>
          <input
            matInput
            type="email"
            formControlName="email"
            autocomplete="off"
            [readonly]="isEditMode"
          />
          <mat-error *ngIf="hasError('email', 'required')"
            >Email required</mat-error
          >
          <mat-error *ngIf="hasError('email', 'email')"
            >Invalid email provided</mat-error
          >
        </mat-form-field>
        <mat-form-field hideRequiredMarker="true" *ngIf="!isEditMode">
          <mat-label>Password</mat-label>
          <input
            matInput
            [type]="showPassword ? 'text' : 'password'"
            #password
            minlength="8"
            maxlength="256"
            formControlName="password"
            autocomplete="off"
          />
          <mat-icon
            matSuffix
            class="text-black cursor-pointer"
            (click)="showPassword = !showPassword"
            >{{ showPassword ? 'visibility_off' : 'visibility' }}</mat-icon
          >
          <mat-hint *ngIf="passwordLength > 128" align="end"
            >{{ passwordLength }}/256</mat-hint
          >
          <mat-error *ngIf="hasError('password', 'required')"
            >Password required</mat-error
          >
          <mat-error *ngIf="hasError('password', 'minlength')"
            >Password must be at least 8 characters ({{
              passwordLength
            }}/8)</mat-error
          >
          <mat-error *ngIf="hasError('password', 'maxlength')"
            >Password must be no more than 256 characters ({{
              passwordLength
            }}/256)</mat-error
          >
          <mat-error *ngIf="hasError('password', 'mustMatch')"
            >Passwords must match</mat-error
          >
        </mat-form-field>
        <!-- confirm password field -->
        <mat-form-field hideRequiredMarker="true" *ngIf="!isEditMode">
          <mat-label>Confirm Password</mat-label>
          <input
            matInput
            [type]="showConfirmPassword ? 'text' : 'password'"
            minlength="8"
            maxlength="256"
            formControlName="confirmPassword"
            autocomplete="off"
            (change)="isMatch('password', 'confirmPassword')"
          />
          <mat-icon
            matSuffix
            class="text-black cursor-pointer"
            (click)="showConfirmPassword = !showConfirmPassword"
            >{{
              showConfirmPassword ? 'visibility_off' : 'visibility'
            }}</mat-icon
          >
          <mat-hint *ngIf="passwordLength > 128" align="end"
            >{{ passwordLength }}/256</mat-hint
          >
          <mat-error *ngIf="hasError('confirmPassword', 'required')"
            >Confirm Password required</mat-error
          >
          <mat-error *ngIf="hasError('confirmPassword', 'match')"
            >Passwords are not matching</mat-error
          >
        </mat-form-field>

        <mat-form-field hideRequiredMarker="true">
          <mat-label>First Name</mat-label>
          <input
            matInput
            type="text"
            formControlName="firstName"
            autocomplete="off"
          />
          <mat-error *ngIf="hasError('firstName', 'required')"
            >First Name required</mat-error
          >
        </mat-form-field>
        <mat-form-field hideRequiredMarker="true">
          <mat-label>Last Name</mat-label>
          <input
            matInput
            type="text"
            formControlName="lastName"
            autocomplete="off"
          />
          <mat-error *ngIf="hasError('lastName', 'required')"
            >Last Name required</mat-error
          >
        </mat-form-field>

        <div class="tix-register-error-placeholder" *ngIf="error | async">
          <span *ngIf="registerAttempted && (error | async)">{{
            error | async
          }}</span>
        </div>
        <button
          matRipple
          *ngIf="!isEditMode"
          [disabled]="
            (!isEditMode && registerForm.invalid) ||
            (isEditMode && registerForm.pristine)
          "
          class="w-full p-3 mt-2 flex items-center space-x-2 cursor-pointer justify-center rounded-md bg-primary text-white font-medium text-md hover:bg-matprimary-700"
          (click)="register()"
        >
          <span *tixBtnLoading="isLoading"></span>
          <span>{{ isLoading ? 'Loading...' : 'Create Account' }}</span>
        </button>
      </form>

      <ng-template [ngIf]="!isEditMode">
        <div class="my-4 flex items-center gap-4">
          <hr class="w-full border-[#72787E4D]" />
          <p class="text-sm text-[#72787E] text-center">or</p>
          <hr class="w-full border-[#72787E4D]" />
        </div>
        <div class="tix-register-with-buttons">
          <button
            matRipple
            aria-label="Google"
            class="w-full p-2.5 flex items-center justify-center rounded-md border-2 border-primary hover:bg-matprimary-50"
          >
            <tix-icon class="mr-4" svgIcon="google"></tix-icon>
            <span class="font-medium text-md text-primary"
              >Register with Google</span
            >
          </button>
          <!-- <button tix-flat-border-button aria-label="Facebook">
            <tix-icon svgIcon="facebook"></tix-icon>
          </button>
          <button tix-flat-border-button aria-label="Apple">
            <tix-icon svgIcon="apple"></tix-icon>
          </button>
          <button tix-flat-border-button aria-label="Twitter">
            <tix-icon svgIcon="twitter"></tix-icon>
          </button> -->
        </div>
      </ng-template>
    </tix-card-content>

    <!-- Edit Mode with Tabs -->
    <div
      *ngIf="isEditMode"
      class="bg-white rounded-md border border-gray-200 shadow-sm"
    >
      <mat-tab-group class="account-admin-tabs" animationDuration="300ms">
        <!-- Tab 1: Basic Information -->
        <mat-tab label="Basic Information">
          <div class="tab-content p-6">
            <div class="section-card">
              <div class="section-content">
                <form [formGroup]="registerForm" class="flex flex-col">
                  <mat-form-field hideRequiredMarker="true">
                    <mat-label>Email</mat-label>
                    <input
                      matInput
                      type="email"
                      formControlName="email"
                      autocomplete="off"
                      [readonly]="isEditMode"
                    />
                    <mat-error *ngIf="hasError('email', 'required')"
                      >Email required</mat-error
                    >
                    <mat-error *ngIf="hasError('email', 'email')"
                      >Invalid email provided</mat-error
                    >
                  </mat-form-field>

                  <mat-form-field hideRequiredMarker="true">
                    <mat-label>First Name</mat-label>
                    <input
                      matInput
                      type="text"
                      formControlName="firstName"
                      autocomplete="off"
                    />
                    <mat-error *ngIf="hasError('firstName', 'required')"
                      >First Name required</mat-error
                    >
                  </mat-form-field>

                  <mat-form-field hideRequiredMarker="true">
                    <mat-label>Last Name</mat-label>
                    <input
                      matInput
                      type="text"
                      formControlName="lastName"
                      autocomplete="off"
                    />
                    <mat-error *ngIf="hasError('lastName', 'required')"
                      >Last Name required</mat-error
                    >
                  </mat-form-field>

                  <div class="flex flex-col space-y-5">
                    <div class="flex flex-col space-y-2">
                      <div class="flex items-start justify-between">
                        <mat-form-field
                          hideRequiredMarker="true"
                          class="flex-1"
                        >
                          <mat-label>Profile Picture</mat-label>
                          <input
                            matInput
                            type="text"
                            formControlName="profilePicture"
                          />
                          <mat-error>Invalid profile picture</mat-error>
                        </mat-form-field>
                        <img
                          class="ml-2 h-[3.6rem] w-[3.6rem] sm:h-[3.85rem] sm:w-[3.85rem] rounded-md object-cover"
                          *ngIf="
                            registerForm.controls.profilePicture.value as pfp
                          "
                          [src]="pfp"
                          alt="Profile picture preview"
                        />
                        <button
                          tix-button
                          (click)="openUploadDialog()"
                          color="light"
                          class="!ring-0 mt-1 ml-2 h-14 w-14 hover:!bg-gray-100 bg-transparent"
                        >
                          <tix-icon
                            svgIcon="upload"
                            class="text-primary"
                          ></tix-icon>
                        </button>
                      </div>
                      <div class="flex items-center justify-end">
                        <button
                          mat-button
                          (click)="updateUserInfo()"
                          class="bg-primary text-white w-40"
                          [ngClass]="[
                            (!isEditMode && registerForm.invalid) ||
                            (isEditMode && registerForm.pristine) ||
                            isLoading
                              ? 'cursor-not-allowed'
                              : 'cursor-pointer',
                            (!isEditMode && registerForm.invalid) ||
                            (isEditMode && registerForm.pristine && !isLoading)
                              ? 'hover:bg-gray-300 hover:text-gray-700 hover:opacity-50'
                              : 'hover:bg-primary-700'
                          ]"
                          [disabled]="
                            (!isEditMode && registerForm.invalid) ||
                            (isEditMode && registerForm.pristine)
                          "
                        >
                          <div *ngIf="!isLoading" class="flex items-center">
                            <span
                              *ngIf="!isLoading"
                              class="material-icons-outlined mr-1"
                              >save</span
                            >
                            <span>Save Changes</span>
                          </div>
                          <div
                            *ngIf="isLoading"
                            class="flex items-center space-x-2 justify-center"
                          >
                            <span
                              class="mr-1"
                              *tixBtnLoading="isLoading"
                            ></span>
                            <span>Loading</span>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </mat-tab>

        <!-- Tab 2: Phone -->
        <mat-tab label="Phone">
          <div class="tab-content p-6">
            <div class="section-card">
              <div class="section-content">
                <tix-phone-numbers-edit
                  [pageName]="'registration'"
                  [showSkipButton]="!isEditMode"
                  [isloading]="isSavingPhoneNumbers$ | async"
                  [phoneNumbers]="userPhoneNumbers$ | async"
                  [phoneTypesBusinessCodeGroup]="businessPhoneTypes$ | async"
                  (delete)="deletePhoneNumber($event)"
                  (save)="savePhoneNumbers($event)"
                  (skipEvent)="next($event)"
                >
                </tix-phone-numbers-edit>
              </div>
            </div>
          </div>
        </mat-tab>

        <!-- Tab 3: Address -->
        <mat-tab label="Address">
          <div class="tab-content p-6">
            <div class="section-card">
              <div class="section-content">
                <div
                  *ngIf="userLoaddingAddress$ | async"
                  class="flex justify-center mt-11"
                >
                  <mat-spinner></mat-spinner>
                </div>
                <tix-address-edit
                  [addresses]="userAddress$ | async"
                  [showSkipButton]="!isEditMode"
                  [countries]="countryCode$ | async"
                  [states]="statesCode$ | async"
                  [isloading]="userLoaddingAddress$ | async"
                  *ngIf="
                    (userLoaddingAddress$ | async) !== true &&
                    (userAddress$ | async) &&
                    (statesCode$ | async) &&
                    (userId$ | async)
                  "
                  (save)="
                    isEditMode ? updateAddress($event) : saveAddress($event)
                  "
                  (skipEvent)="next($event)"
                  [userId]="(userId$ | async) ?? ''"
                >
                </tix-address-edit>
              </div>
            </div>
          </div>
        </mat-tab>
      </mat-tab-group>
    </div>
  </ng-container>

  <ng-template [ngIf]="step === 2">
    <tix-phone-numbers-edit
      [pageName]="'registration'"
      [showSkipButton]="!isEditMode"
      [isloading]="isSavingPhoneNumbers$ | async"
      [phoneNumbers]="userPhoneNumbers$ | async"
      [phoneTypesBusinessCodeGroup]="businessPhoneTypes$ | async"
      (delete)="deletePhoneNumber($event)"
      (save)="savePhoneNumbers($event)"
      (skipEvent)="next($event)"
    >
    </tix-phone-numbers-edit>
  </ng-template>

  <ng-template [ngIf]="step === 3">
    <div *ngIf="userLoaddingAddress$ | async" class="flex justify-center mt-11">
      <mat-spinner></mat-spinner>
    </div>
    <tix-address-edit
      [addresses]="userAddress$ | async"
      [showSkipButton]="!isEditMode"
      [countries]="countryCode$ | async"
      [states]="statesCode$ | async"
      [isloading]="userLoaddingAddress$ | async"
      *ngIf="
        (userLoaddingAddress$ | async) !== true &&
        (userAddress$ | async) &&
        (statesCode$ | async) &&
        (userId$ | async)
      "
      (save)="isEditMode ? updateAddress($event) : saveAddress($event)"
      (skipEvent)="next($event)"
      [userId]="(userId$ | async) ?? ''"
    >
    </tix-address-edit>
  </ng-template>
</div>
<div class="tix-card tix-register-card tix-register-action" *ngIf="step !== 1">
  <!-- <a href="javascript:void(0)" (click)="next()">Skip</a> -->
</div>
